import { toast as st } from "sonner";
import type { ReactNode } from "react";

export type ToastId = string | number;

// Options type from sonner - allowing flexible configuration
type ToastOptions = {
  id?: ToastId;
  duration?: number;
  position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  cancel?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
  onAutoClose?: () => void;
  important?: boolean;
  [key: string]: unknown;
};

export function notifySuccess(content: ReactNode, options?: ToastOptions) {
  return st.success(content, options);
}

export function notifyError(content: ReactNode, options?: ToastOptions) {
  return st.error(content, options);
}

export function notifyInfo(content: ReactNode, options?: ToastOptions) {
  return st.info(content, options);
}

export function notifyWarning(content: ReactNode, options?: ToastOptions) {
  return st.warning(content, options);
}

export function notifyLoading(content: ReactNode, options?: ToastOptions) {
  return st.loading(content, options);
}

export function updateToast(
  id: ToastId,
  content: ReactNode,
  type: "success" | "error" | "info" | "warning" = "success",
  options?: ToastOptions,
) {
  return st[type](content, { id, ...options });
}

export function dismissToast(id?: ToastId) {
  st.dismiss(id);
}

// Keep an object named `toast` for minimal diff when replacing imports
export const toast = {
  success: notifySuccess,
  error: notifyError,
  info: notifyInfo,
  warn: notifyWarning,
  loading: notifyLoading,
  update: updateToast,
  dismiss: dismissToast,
};
